import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { adminService, MissingStock } from '../../services/adminService';

const StockManagementTab: React.FC = () => {
  const [missingStocks, setMissingStocks] = useState<MissingStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [totalCount, setTotalCount] = useState(0);
  
  // Table pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStock, setEditingStock] = useState<MissingStock | null>(null);
  const [editForm, setEditForm] = useState<Partial<MissingStock>>({});
  
  // Add dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState<Partial<MissingStock>>({});

  useEffect(() => {
    loadMissingStocks();
  }, [page, rowsPerPage, searchTerm, statusFilter]);

  const loadMissingStocks = async () => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      console.log('🔍 Loading missing stocks with params:', { page: page + 1, rowsPerPage, searchTerm, statusFilter });
      
      const response = await adminService.getMissingStocks(page + 1, rowsPerPage, searchTerm, statusFilter);
      console.log('✅ Missing stocks response:', response);
      
      setMissingStocks(response.stocks);
      setTotalCount(response.pagination.total);
    } catch (err: any) {
      console.error('❌ Error loading missing stocks:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load missing stocks data';
      setError(`Failed to load missing stocks data: ${errorMessage}`);
      toast.error(`Failed to load missing stocks: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditStock = (stock: MissingStock) => {
    setEditingStock(stock);
    setEditForm({ ...stock });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingStock || !editForm.symbol) {
      toast.error('Symbol is required');
      return;
    }

    try {
      await adminService.updateMissingStock(editingStock.symbol, editForm);
      await loadMissingStocks(); // Reload data
      
      toast.success('Stock updated successfully');
      setEditDialogOpen(false);
      setEditingStock(null);
      setEditForm({});
    } catch (err: any) {
      toast.error('Failed to update stock');
    }
  };

  const handleDeleteStock = async (symbol: string) => {
    if (!window.confirm(`Are you sure you want to delete ${symbol}?`)) {
      return;
    }

    try {
      await adminService.deleteMissingStock(symbol);
      await loadMissingStocks(); // Reload data
      
      toast.success('Stock deleted successfully');
    } catch (err: any) {
      toast.error('Failed to delete stock');
    }
  };

  const handleAddStock = async () => {
    if (!addForm.symbol) {
      toast.error('Symbol is required');
      return;
    }

    try {
      const newStock: MissingStock = {
        symbol: addForm.symbol,
        exchange: addForm.exchange || 'NS',
        status: addForm.status || 'active',
        corrected_symbol: addForm.corrected_symbol,
        notes: addForm.notes,
      };
      
      await adminService.addMissingStock(newStock);
      await loadMissingStocks(); // Reload data
      
      toast.success('Stock added successfully');
      setAddDialogOpen(false);
      setAddForm({});
    } catch (err: any) {
      toast.error('Failed to add stock');
    }
  };

  // Stocks are already filtered and paginated by the API
  const paginatedStocks = missingStocks;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delisted':
        return 'error';
      case 'symbol_change':
        return 'warning';
      case 'active':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'delisted':
        return 'Delisted';
      case 'symbol_change':
        return 'Symbol Change';
      case 'active':
        return 'Active';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Stock Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {missingStocks.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Missing Stocks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error">
                {missingStocks.filter(s => s.status === 'delisted').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Delisted Stocks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {missingStocks.filter(s => s.status === 'symbol_change').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Symbol Changes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {missingStocks.filter(s => s.status === 'active').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Stocks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search stocks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="delisted">Delisted</MenuItem>
                <MenuItem value="symbol_change">Symbol Change</MenuItem>
                <MenuItem value="active">Active</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={12} md={5}>
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadMissingStocks}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
              >
                Add Stock
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Stocks Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Symbol</TableCell>
              <TableCell>Exchange</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Corrected Symbol</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedStocks.map((stock) => (
              <TableRow key={stock.symbol} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {stock.symbol}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={stock.exchange} 
                    size="small" 
                    variant="outlined"
                    color={stock.exchange === 'NS' ? 'primary' : 'secondary'}
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={getStatusLabel(stock.status)} 
                    size="small" 
                    color={getStatusColor(stock.status) as any}
                  />
                </TableCell>
                <TableCell>
                  {stock.corrected_symbol && (
                    <Typography variant="body2" color="primary">
                      {stock.corrected_symbol}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {stock.notes}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit Stock">
                    <IconButton 
                      size="small" 
                      onClick={() => handleEditStock(stock)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Stock">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteStock(stock.symbol)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Edit Stock Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Stock</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Symbol"
                value={editForm.symbol || ''}
                onChange={(e) => setEditForm({ ...editForm, symbol: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Exchange</InputLabel>
                <Select
                  value={editForm.exchange || 'NS'}
                  label="Exchange"
                  onChange={(e) => setEditForm({ ...editForm, exchange: e.target.value as 'NS' | 'BO' })}
                >
                  <MenuItem value="NS">NSE (.NS)</MenuItem>
                  <MenuItem value="BO">BSE (.BO)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editForm.status || 'active'}
                  label="Status"
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value as any })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="symbol_change">Symbol Change</MenuItem>
                  <MenuItem value="delisted">Delisted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Corrected Symbol"
                value={editForm.corrected_symbol || ''}
                onChange={(e) => setEditForm({ ...editForm, corrected_symbol: e.target.value })}
                helperText="Enter the new symbol if this is a symbol change"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={editForm.notes || ''}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" startIcon={<SaveIcon />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Stock Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Stock</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Symbol"
                value={addForm.symbol || ''}
                onChange={(e) => setAddForm({ ...addForm, symbol: e.target.value })}
                placeholder="e.g., RELIANCE.NS"
              />
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Exchange</InputLabel>
                <Select
                  value={addForm.exchange || 'NS'}
                  label="Exchange"
                  onChange={(e) => setAddForm({ ...addForm, exchange: e.target.value as 'NS' | 'BO' })}
                >
                  <MenuItem value="NS">NSE (.NS)</MenuItem>
                  <MenuItem value="BO">BSE (.BO)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={addForm.status || 'active'}
                  label="Status"
                  onChange={(e) => setAddForm({ ...addForm, status: e.target.value as any })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="symbol_change">Symbol Change</MenuItem>
                  <MenuItem value="delisted">Delisted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Corrected Symbol"
                value={addForm.corrected_symbol || ''}
                onChange={(e) => setAddForm({ ...addForm, corrected_symbol: e.target.value })}
                helperText="Enter the new symbol if this is a symbol change"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={addForm.notes || ''}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button onClick={handleAddStock} variant="contained" startIcon={<AddIcon />}>
            Add Stock
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockManagementTab;
